import { useWindowDimensions } from "@/platform/dimensions";
import { useEffect, useRef } from "react";
import tirvuGif from "@/assets/images/tirvu.gif";
import s from "./Splash.module.css";

/** Easing.out(Easing.back(k)) do reanimated — amostrado em keyframes da Web Animations API. */
function easeOutBack(t: number, k = 1.1): number {
  const u = 1 - t;
  return 1 - u * u * ((k + 1) * u - k);
}

export default function Splash({ onDone, onReady }: { onDone: () => void; onReady?: () => void }) {
  const { width } = useWindowDimensions();
  // WEB: em tablet/desktop a janela pode ser muito larga; limita a base da conta para o
  // logo não estourar. Abaixo de 768px é exatamente a mesma conta do app.
  const base = width < 768 ? width : Math.min(width, 480);
  const gifWidth = base * 1.6;
  const gifHeight = gifWidth / 3.8;

  const logoRef = useRef<HTMLDivElement>(null);

  // WEB: a animação de entrada roda uma vez (no RN o withTiming re-disparado parte do
  // valor atual, então um novo onDone não "reinicia" o logo); só o timer depende do onDone.
  useEffect(() => {
    const t = setTimeout(onDone, 1200);
    return () => clearTimeout(t);
  }, [onDone]);

  useEffect(() => {
    const el = logoRef.current;
    let animOpacity: Animation | undefined;
    let animScale: Animation | undefined;
    if (el && typeof el.animate === "function") {
      // opacity: withTiming(1, { duration: 600, easing: Easing.out(Easing.cubic) })
      animOpacity = el.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 600,
        easing: "cubic-bezier(0.215, 0.61, 0.355, 1)",
        fill: "forwards",
      });
      // scale: withTiming(1, { duration: 800, easing: Easing.out(Easing.back(1.1)) })
      const passos = 24;
      const frames: Keyframe[] = [];
      for (let i = 0; i <= passos; i++) {
        const t = i / passos;
        frames.push({ offset: t, transform: `scale(${0.9 + 0.1 * easeOutBack(t)})` });
      }
      animScale = el.animate(frames, { duration: 800, easing: "linear", fill: "forwards" });
    } else if (el) {
      el.style.opacity = "1";
      el.style.transform = "none";
    }
    return () => {
      animOpacity?.cancel();
      animScale?.cancel();
    };
  }, []);

  // Avisa que o fundo (#294956) já está pintado → o caller esconde o splash NATIVO
  // exatamente agora, sem frame branco no meio. Dispara uma única vez.
  // WEB: sem onLayout; dispara no 1º frame após a montagem (equivalente ao 1º layout).
  const readyRef = useRef(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    if (readyRef.current) return;
    readyRef.current = true;
    requestAnimationFrame(() => onReadyRef.current?.());
  }, []);

  return (
    <div className={s.container}>
      <div ref={logoRef} className={s.logo}>
        <img
          src={tirvuGif}
          alt=""
          className={s.gif}
          style={{ width: gifWidth, height: gifHeight }}
          draggable={false}
        />
      </div>
    </div>
  );
}
